import { Address, Dashboard } from '@vfde-sails/avc-core';

/**
 * Convert any number or string into a string which can be displayed on frontend
 * @param {number|string} price - Price to convert.
 * @param {boolean} [showEmptyCents=false] - Show the ",00" part? (default=false)
 * @returns Price string.
 */
export const convertToPriceString = (price: number | string, showEmptyCents: boolean = false) => {
    const priceNumber = +(price.toString().replace(',', '.'));
    return priceNumber.toFixed(!showEmptyCents && Number.isInteger(priceNumber) ? 0 : 2).replace('.', ',');
};

/**
 * Formats the name of the customer from the customer `dashboard`.
 * @param {Dashboard} dashboad - Customer related information from their `dashboard`.
 * @returns {string} The formatted customer name.
 */
export const getCustomerFullName = ({ firstName, lastName }: Dashboard): string =>
    `${firstName} ${lastName}`.trim();

/**
 * Formats the city of the customer.
 * @param {Address} address - The address of the customer.
 * @returns {string} The formatted city name.
 */
export const getCustomerCity = ({ city, zipcode }: Address): string =>
    `${zipcode ?? ''} ${city ?? ''}`.trim();
/**
 * Formats the street of the customer.
 * @param {Address} address - The address of the customer.
 * @returns {string} The formatted street name.
 */
export const getCustomerStreet = ({ street, housenumber, housenumberextra }: Address): string =>
    `${street ?? ''} ${housenumber ?? ''}${housenumberextra ?? ''}`.trim();

/**
 * Converts bits to readable format in mbit
 * @param {number} bits - bits to convert
 * @returns {string} readable formatted bits to mbit
 */
export const formatBitsToMbit = (bits: number): string => {
    const valueInMbit = bits / 1000000;
    return `${valueInMbit} MBit`;
};

/**
 * Formats the given string to lowercase, separates words
 * if they are next to each other without space and removes
 * any additional whitespaces and replaces the special
 * German letters in the text to basic latin letters
 * (e.g. `ß` to `ss`, `ä` to `ae`, and so on).
 * This step is requested by Digital Power,
 * to process the incoming data more easily.
 */
export const formatToTrackingString = (text: string, maxLength = 100): string =>
    text.replace(/([a-z])([A-Z])/g, '$1 $2')
        .toLowerCase()
        .trim()
        .replace(/\s+/g, ' ')
        .substring(0, maxLength)
        .replace(/\u00e4/g, 'ae')
        .replace(/\u00f6/g, 'oe')
        .replace(/\u00fc/g, 'ue')
        .replace(/\u00df/g, 'ss');

/**
* Normalize text by removing leading and trailing whitespace and replacing
* multiple whitespace characters (spaces, tabs, newlines) with a single space.
*
* This is useful for ensuring consistent text comparison in tests where
* formatting differences (such as extra spaces or line breaks) may cause issues.
*
* @param {string} text - The text to be normalized.
* @returns {string} The normalized text.
*
* @example
* // Returns "Hello world"
* normalizeText("  Hello   world\n");
*
* @example
* // Returns "Text with multiple spaces"
* normalizeText("Text    with multiple    spaces");
*/
export function normalizeText(text: string): string {
    return text.trim().replace(/\s+/g, ' ');
}
